import { useMemo } from 'react';
import Button from '@/shared/components/flip-ui-kit/Button';
import AgeWithTooltip from '@/shared/components/molecules/AgeWithTooltip';
import { FlipLabel } from '@/shared/components/molecules/FlipLabel';
import { TableV2 } from '@/shared/components/molecules/TableV2';
import DelegatorLinkCell from '@/shared/components/operators/DelegatorLinkCell';
import { getDelegationRewards } from '@/shared/graphql/explorer/delegation';
import { getWhitelistBlacklistTimestamp } from '@/shared/graphql/explorer/queries';
import { useGqlQuery } from '@/shared/hooks/useGqlQuery';
import { type UseExtrinsicMutationResult } from '@/shared/hooks/useStateChainAccount';
import ForbidIcon from '@/shared/icons/flip-ui-kit/small/Forbid';
import TokenAmount from '@/shared/utils/TokenAmount';

export const WhitelistBlacklistTable = ({
  operatorId,
  delegators,
  whiteOrBlacklistedDelegators,
  openDelegationEnabled,
  remove,
}: {
  operatorId: `cF${string}`;
  delegators: Map<`cF${string}`, TokenAmount>;
  whiteOrBlacklistedDelegators: `cF${string}`[];
  openDelegationEnabled: boolean;
  remove?: UseExtrinsicMutationResult<'operator_allowDelegator' | 'operator_blockDelegator'>;
}) => {
  const { data } = useGqlQuery(getWhitelistBlacklistTimestamp, {
    variables: {
      operatorId,
      delegatorIds: whiteOrBlacklistedDelegators,
      type: openDelegationEnabled ? 'BLOCK_DELEGATOR' : 'ALLOW_DELEGATOR',
    },
    enabled: whiteOrBlacklistedDelegators.length > 0,
  });

  const { data: rewardsData } = useGqlQuery(getDelegationRewards, {
    variables: { ids: [...delegators.keys()] },
    enabled: delegators.size > 0 && openDelegationEnabled === false,
  });

  const rowData = useMemo(
    () =>
      whiteOrBlacklistedDelegators
        .map((delegatorId) => ({
          rewards: TokenAmount.fromAsset(
            rewardsData?.accounts?.nodes
              .find((item) => item.idSs58 === delegatorId)
              ?.rewards.nodes.at(0)?.amount ?? 0,
            'Flip',
          ),
          listedSince: data?.accounts?.nodes.find((acc) => acc.idSs58 === delegatorId)?.actions
            ?.aggregates?.max?.timestamp,
          amount: delegators.get(delegatorId) ?? TokenAmount.ZERO,
          idSs58: delegatorId,
        }))
        .sort((a, b) => b.amount.cmp(a.amount)),
    [delegators, whiteOrBlacklistedDelegators, data, rewardsData],
  );

  return (
    <TableV2
      columns={[
        'Account ID',
        'Delegated',
        'Rewards',
        {
          name: openDelegationEnabled ? 'Blacklisted Since' : 'Whitelisted Since',
          alignment: 'right',
        },
        ...(remove ? [''] : []),
      ]}
      rows={rowData.map(({ idSs58: delegatorId, amount, rewards, listedSince }) => (
        <tr key={delegatorId}>
          <td>
            <DelegatorLinkCell accountId={delegatorId} />
          </td>
          <td>{!openDelegationEnabled ? <FlipLabel amount={amount} /> : 'N/A'}</td>
          <td>{!openDelegationEnabled ? <FlipLabel amount={rewards} prefix="+" /> : 'N/A'}</td>
          <td className="whitespace-nowrap text-right text-cf-light-2">
            {listedSince ? <AgeWithTooltip timestamp={listedSince} /> : '-'}
          </td>
          {remove && (
            <td className="w-[1%]">
              <Button
                type="secondary-standard"
                className="whitespace-nowrap"
                onClick={() => remove.mutate(delegatorId)}
                icon={<ForbidIcon className="mr-1 text-cf-red-2" />}
                loading={remove.isPending && 'iconOnly'}
                disabled="ifLoading"
                iconPos="left"
              >
                Remove from {openDelegationEnabled ? 'Blacklist' : 'Whitelist'}
              </Button>
            </td>
          )}
        </tr>
      ))}
    />
  );
};
